import { Head, usePage, router } from "@inertiajs/react";
import { FormEvent, useEffect, useRef, useState } from "react";
import { ArrowRight, Camera, ChevronDown, Clock, Mail, Sparkles, Star, Users, Zap } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import ProductDemo from "@/components/ProductDemo";
import type { SharedProps } from "@/types";

function scrollToHash() {
  const hash = window.location.hash;
  if (!hash) return;
  const el = document.querySelector(hash);
  if (el) el.scrollIntoView({ behavior: "smooth" });
}

export default function Login() {
  const { flash } = usePage<SharedProps>().props;

  useEffect(() => {
    // Scroll to #entrar or #como-funciona after React mounts
    requestAnimationFrame(scrollToHash);
  }, []);

  const handleGoogleSignIn = (e: FormEvent) => {
    e.preventDefault();
    (e.target as HTMLFormElement).submit();
  };

  const csrfToken =
    document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";

  const loginBtnClass =
    "flex w-full cursor-pointer items-center justify-center gap-3 rounded-xl px-8 py-3.5 text-base font-medium shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 focus:ring-offset-background";

  return (
    <>
      <Head title="fotoCerta - Avaliação de Fotos pra Tinder, Bumble e Apps de Namoro">
        <meta
          name="description"
          content="Descubra como suas fotos são percebidas. Pessoas reais avaliam suas fotos pra Tinder, Bumble e outros apps de namoro. Grátis pra começar."
        />
        <meta property="og:title" content="fotoCerta - Avaliação de Fotos pra Apps de Namoro" />
        <meta
          property="og:description"
          content="Pessoas reais avaliam suas fotos pra Tinder, Bumble e outros apps de namoro. Descubra qual foto usar no perfil."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://fotocerta.com" />
        <meta property="og:image" content="https://fotocerta.com/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="pt_BR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="fotoCerta - Avaliação de Fotos pra Apps de Namoro" />
        <meta name="twitter:description" content="Pessoas reais avaliam suas fotos pra Tinder, Bumble e outros apps de namoro. Descubra qual foto usar no perfil." />
        <meta name="twitter:image" content="https://fotocerta.com/og-image.png" />
        <link rel="canonical" href="https://fotocerta.com" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "fotoCerta",
          "url": "https://fotocerta.com",
          "description": "Avaliações reais de pessoas reais. Descubra como sua foto é percebida em apps de namoro como Tinder, Bumble e Badoo.",
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "fotoCerta",
          "url": "https://fotocerta.com",
          "logo": "https://fotocerta.com/icon.png",
          "sameAs": ["https://instagram.com/fotocerta.app"],
        })}</script>
      </Head>

      <div className="min-h-screen bg-background text-foreground">
        {/* Navbar */}
        <nav className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/20 text-primary">
                <Sparkles size={18} />
              </div>
              <span className="text-lg font-semibold">fotoCerta</span>
            </div>
            <div className="flex items-center gap-4">
              <a
                href="/blog"
                className="text-sm font-medium text-foreground/80 transition-colors hover:text-foreground"
              >
                Blog
              </a>
              <button
                onClick={() => document.getElementById("entrar")?.scrollIntoView({ behavior: "smooth" })}
                className="text-sm font-medium text-foreground/80 transition-colors hover:text-foreground"
              >
                Entrar
              </button>
            </div>
          </div>
        </nav>

        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute left-1/2 top-0 h-[300px] w-[300px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/10 blur-3xl md:h-[600px] md:w-[600px]" />
            <div className="absolute right-0 top-1/3 hidden rounded-full bg-accent/8 blur-3xl md:block md:h-[400px] md:w-[400px]" />
          </div>

          <div className="relative mx-auto max-w-6xl px-4 pb-10 pt-10 sm:pb-14 sm:pt-12 md:pb-28 md:pt-24">
            <div className="mx-auto max-w-3xl text-center">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs sm:mb-6 sm:px-4 sm:py-1.5 sm:text-sm text-primary">
                <Zap size={14} />
                Avaliações reais de pessoas reais — é grátis para começar
              </div>

              <h1 className="text-2xl font-bold leading-tight tracking-tight sm:text-4xl md:text-5xl lg:text-6xl">
                A foto certa muda <span className="text-primary">tudo</span>
              </h1>

              <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground sm:mt-5 sm:text-base md:text-lg">
                Pessoas reais avaliam suas fotos para você saber qual usar no perfil de namoro, rede social ou trabalho.
              </p>

              <div className="mt-6 flex flex-col items-center gap-4 sm:mt-10">
                <div className="flex w-full max-w-sm flex-col gap-3">
                  <form action="/auth/google_oauth2" method="post" onSubmit={handleGoogleSignIn}>
                    <input type="hidden" name="authenticity_token" value={csrfToken} />
                    <button type="submit" className={`${loginBtnClass} bg-primary text-primary-foreground hover:brightness-110`}>
                      <GoogleIcon />
                      Começar agora — é grátis
                    </button>
                  </form>
                  <EmailLoginForm csrfToken={csrfToken} loginBtnClass={loginBtnClass} />
                </div>
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Clock size={12} />
                  Resultados em menos de 24h
                </p>
                <button
                  onClick={() => document.getElementById("como-funciona")?.scrollIntoView({ behavior: "smooth" })}
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  Como funciona?
                </button>
              </div>
            </div>

            {/* Social proof stats */}
            <div className="mx-auto mt-8 grid max-w-2xl grid-cols-3 gap-4 sm:mt-12 md:mt-16">
              <div className="text-center">
                <p className="text-xl font-bold sm:text-2xl md:text-3xl">12.400+</p>
                <p className="mt-1 text-xs text-muted-foreground sm:text-sm">fotos avaliadas</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold sm:text-2xl md:text-3xl">52.000+</p>
                <p className="mt-1 text-xs text-muted-foreground sm:text-sm">votos dados</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold sm:text-2xl md:text-3xl">4,8<span className="text-base font-normal text-primary sm:text-lg">/5</span></p>
                <p className="mt-1 text-xs text-muted-foreground sm:text-sm">satisfação dos usuários</p>
              </div>
            </div>

            {/* Product demo */}
            <div className="mt-4 sm:mt-8 md:mt-12">
              <ProductDemo />
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section id="como-funciona" className="mx-auto max-w-6xl px-4 py-10 sm:py-14 md:py-28">
          <div className="text-center">
            <h2 className="text-xl font-bold tracking-tight sm:text-3xl md:text-4xl">
              Como funciona
            </h2>
            <p className="mx-auto mt-2 max-w-lg text-sm text-muted-foreground sm:mt-4 sm:text-base">
              Três passos para saber se sua foto funciona.
            </p>
          </div>

          <div className="mt-6 grid gap-3 sm:mt-10 sm:gap-0 md:mt-14 md:grid-cols-[1fr_auto_1fr_auto_1fr]">
            <StepCard
              step={1}
              icon={<Camera size={24} />}
              title="Envie sua foto"
              description="Escolha o contexto: namoro, rede social ou trabalho."
            />
            <div className="hidden items-center justify-center md:flex">
              <ArrowRight size={20} className="text-muted-foreground/40" />
            </div>
            <StepCard
              step={2}
              icon={<Users size={24} />}
              title="Pessoas reais avaliam"
              description="Avaliadores de verdade dão notas na sua foto. Sem robôs, sem algoritmo."
            />
            <div className="hidden items-center justify-center md:flex">
              <ArrowRight size={20} className="text-muted-foreground/40" />
            </div>
            <StepCard
              step={3}
              icon={<Star size={24} />}
              title="Veja seus resultados"
              description="Descubra se sua foto passa a impressão que você quer. Resultados em menos de 24h."
            />
          </div>
        </section>

        {/* Secondary Login Section */}
        <section id="entrar" className="mx-auto max-w-6xl px-4 py-10 sm:py-14 md:py-20">
          <div className="mx-auto max-w-lg text-center">
            {flash?.alert && (
              <Alert variant="error" className="mb-6">
                <AlertDescription>{flash.alert}</AlertDescription>
              </Alert>
            )}
            {flash?.notice && (
              <Alert variant="success" className="mb-6">
                <AlertDescription>{flash.notice}</AlertDescription>
              </Alert>
            )}

            <h2 className="text-xl font-bold tracking-tight sm:text-2xl">Pronto para descobrir?</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Crie sua conta em segundos e comece a avaliar.
            </p>

            <div className="mx-auto mt-6 flex w-full max-w-sm flex-col gap-3">
              <form action="/auth/google_oauth2" method="post" onSubmit={handleGoogleSignIn}>
                <input type="hidden" name="authenticity_token" value={csrfToken} />
                <button type="submit" className={`${loginBtnClass} bg-primary text-primary-foreground hover:brightness-110`}>
                  <GoogleIcon />
                  Começar agora — é grátis
                </button>
              </form>
              <EmailLoginForm csrfToken={csrfToken} loginBtnClass={loginBtnClass} />
            </div>

            <p className="mt-4 text-xs text-muted-foreground">
              Usamos apenas seu nome e email. Nunca postamos nada.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className="mx-auto max-w-6xl px-4 py-10 sm:py-14 md:py-20">
          <div className="text-center">
            <h2 className="text-xl font-bold tracking-tight sm:text-3xl md:text-4xl">
              Perguntas frequentes
            </h2>
          </div>
          <div className="mx-auto mt-6 max-w-2xl sm:mt-10">
            <FaqItem question="É grátis mesmo?">
              Sim. Você ganha créditos avaliando fotos de outros usuários e usa esses créditos para enviar as suas. Não precisa pagar nada para começar.
            </FaqItem>
            <FaqItem question="Quem vê minha foto?">
              Apenas usuários reais da plataforma que estão avaliando fotos. Sua foto nunca é compartilhada fora do fotoCerta e as avaliações são 100% anônimas.
            </FaqItem>
            <FaqItem question="Como funciona a avaliação?">
              Pessoas reais dão nota de 1 a 5 em três critérios: se a foto parece divertida, confiável e atraente. Você recebe a média das notas quando todas as avaliações chegarem.
            </FaqItem>
            <FaqItem question="Quanto tempo leva pra receber resultados?">
              A maioria dos usuários recebe resultados completos em menos de 24 horas.
            </FaqItem>
            <FaqItem question="Minhas fotos ficam seguras?">
              Suas fotos são armazenadas com criptografia e nunca são usadas para outros fins. Você pode deletar sua foto a qualquer momento.
            </FaqItem>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-border bg-background/80">
          <div className="mx-auto flex max-w-6xl flex-col items-center gap-3 px-4 py-6 text-xs text-muted-foreground sm:flex-row sm:justify-between">
            <span>&copy; {new Date().getFullYear()} fotoCerta. Feito no Brasil.</span>
            <div className="flex gap-4">
              <a href="/blog" className="transition-colors hover:text-foreground">
                Blog
              </a>
              <span className="text-border">|</span>
              <a href="mailto:contato@fotocerta.com" className="transition-colors hover:text-foreground">
                Contato
              </a>
              <span className="text-border">|</span>
              <a href="https://instagram.com/fotocerta.app" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-foreground">
                Instagram
              </a>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

/* ---------- Sub-components ---------- */

function EmailLoginForm({ csrfToken, loginBtnClass }: { csrfToken: string; loginBtnClass: string }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    router.post("/magic_link", { email, authenticity_token: csrfToken }, {
      onFinish: () => setLoading(false),
    });
  };

  if (!open) {
    return (
      <button
        onClick={() => {
          setOpen(true);
          setTimeout(() => inputRef.current?.focus(), 50);
        }}
        className={`${loginBtnClass} border border-border bg-card text-foreground hover:bg-secondary`}
      >
        <Mail size={18} />
        Entrar com email
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex w-full items-center gap-2">
      <input
        ref={inputRef}
        type="email"
        required
        placeholder="seu@email.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-[3px] focus:ring-ring/20"
      />
      <button
        type="submit"
        disabled={loading}
        className="shrink-0 rounded-lg bg-secondary px-4 py-2 text-sm font-medium text-secondary-foreground transition-colors hover:bg-secondary/80 disabled:opacity-50"
      >
        {loading ? "Enviando…" : "Enviar link"}
      </button>
    </form>
  );
}

function GoogleIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

function FaqItem({ question, children }: { question: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full cursor-pointer items-center justify-between py-4 text-left text-sm font-medium sm:text-base"
      >
        {question}
        <ChevronDown
          size={18}
          className={`shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <p className="pb-4 text-sm leading-relaxed text-muted-foreground">{children}</p>
      )}
    </div>
  );
}

function StepCard({
  step,
  icon,
  title,
  description,
}: {
  step: number;
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="group relative flex items-start gap-4 rounded-2xl border border-border bg-card p-4 sm:block sm:p-8">
      <div className="absolute right-3 top-3 text-3xl font-black text-foreground/[0.04] sm:right-6 sm:top-6 sm:text-5xl">
        {step}
      </div>
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary sm:mb-4 sm:h-12 sm:w-12">
        {icon}
      </div>
      <div>
        <h3 className="text-base font-semibold sm:text-lg">{title}</h3>
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground sm:mt-2">{description}</p>
      </div>
    </div>
  );
}

